import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Login.css';

interface LocationState {
  from?: string;
}

const Login = () => {
  const { 
    signUpWithEmail, 
    signInWithEmail 
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  const from = state?.from || '/';
  
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userExists, setUserExists] = useState(false);

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setUserExists(false);

    try {
      let result;
      if (isSignUp) {
        result = await signUpWithEmail(email, password, name);
      } else {
        result = await signInWithEmail(email, password);
      }

      if (result?.error) {
        // Check if the error indicates user already exists
        if (isSignUp && (
          result.error.message.includes('User already registered') ||
          result.error.message.includes('already exists') ||
          result.error.message.includes('already registered')
        )) {
          setUserExists(true);
          setError('An account with this email already exists.');
        } else {
          setError(result.error.message);
        }
      } else if (isSignUp) {
        setError('Please check your email to confirm your account before signing in.');
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    }
    
    setLoading(false);
  };

  const toggleSignUp = () => {
    setIsSignUp(!isSignUp);
    setError(null);
    setUserExists(false);
  };

  const switchToLogin = () => {
    setIsSignUp(false);
    setError(null);
    setUserExists(false);
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1>Share My Shed</h1>
        <p className="login-description">
          {from === '/' 
            ? 'Join our community to borrow and lend tools and equipment'
            : 'Please sign in to continue to checkout'}
        </p>
        
        <form onSubmit={handleEmailAuth} className="email-form">
          {isSignUp && (
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required={isSignUp}
                disabled={loading}
                placeholder="Enter your full name"
              />
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              placeholder="Enter your email"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              placeholder="Enter your password"
              minLength={6}
            />
          </div>
          
          {error && (
            <div className="error-message">
              {error}
              {userExists && (
                <div className="user-exists-message">
                  <button 
                    type="button"
                    className="switch-to-login-button"
                    onClick={switchToLogin}
                  >
                    Click here to sign in instead
                  </button>
                </div>
              )}
            </div>
          )}
          
          <button 
            type="submit" 
            className="submit-button"
            disabled={loading}
          >
            {loading ? 'Loading...' : (isSignUp ? 'Sign Up' : 'Sign In')}
          </button>
          
          <div className="form-footer">
            <button 
              type="button"
              className="toggle-mode-button"
              onClick={toggleSignUp}
              disabled={loading}
            >
              {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
            </button>
          </div>
        </form>

        <div className="login-footer">
          <p>By signing in, you agree to our Terms of Service and Privacy Policy</p>
        </div>
      </div>
    </div>
  );
};

export default Login; 